import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import OptiBuildLogo from './OptibuildLogo';
import { ArrowLeft, CheckCircle, AlertCircle, Mail, Loader2, RefreshCw } from 'lucide-react';
import { emailActivationService, ValidateCodeRequest } from '../src/services/email-activation.service';

interface ActivationPageProps {
  onBackToLogin: () => void;
  onActivationComplete: () => void;
  /** Called when the backend returns a set_password_token after email verification */
  onSetPassword: (token: string) => void;
}

export function ActivationPage({ onBackToLogin, onActivationComplete, onSetPassword }: ActivationPageProps) {
  const [email, setEmail] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState<'enter-email' | 'enter-code' | 'success'>('enter-email');

  useEffect(() => {
    const params = emailActivationService.extractActivationParams();
    if (!params) return;

    if (params.email) setEmail(params.email);
    setActivationCode(params.code);
    setStep('enter-code');

    // Both email and code in URL → auto-validate immediately (link-click happy path)
    if (params.email && params.code) {
      validateFromLink(params.email, params.code);
    }
  }, []);

  const validateFromLink = async (emailParam: string, codeParam: string) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await emailActivationService.validateActivationCode({
        email: emailParam.toLowerCase().trim(),
        activation_code: codeParam.toUpperCase().trim(),
      });
      if (result.success && result.data.email_verified) {
        if (result.data.set_password_token) {
          onSetPassword(result.data.set_password_token);
          return;
        }
        setStep('success');
      } else {
        setError('This activation link is invalid. Please enter your code below.');
      }
    } catch (err: any) {
      if (err.error === 'already_activated') {
        setTimeout(() => onActivationComplete(), 1500);
        return;
      }
      setError(err.message || 'Activation failed. Please enter your code below.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendActivation = async () => {
    if (!email || !emailActivationService.isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      const result = await emailActivationService.sendActivationEmail({
        email: email.toLowerCase().trim(),
        template: 'welcome'
      });

      if (result.success) {
        setSuccess('Activation email sent! Please check your inbox.');
        setStep('enter-code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send activation email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleValidateCode = async () => {
    if (!activationCode || !emailActivationService.isValidActivationCode(activationCode)) {
      setError('Please enter a valid 6-8 character activation code');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const request: ValidateCodeRequest = {
        email: email.toLowerCase().trim(),
        activation_code: activationCode.toUpperCase().trim()
      };

      const result = await emailActivationService.validateActivationCode(request);

      if (result.success && result.data.email_verified) {
        if (result.data.set_password_token) {
          onSetPassword(result.data.set_password_token);
          return;
        }
        setStep('success');
      } else {
        setError('Invalid activation code. Please check and try again.');
      }
    } catch (err: any) {
      if (err.error === 'invalid_code') {
        setError('Invalid activation code. Please check and try again.');
      } else if (err.error === 'expired_code') {
        setError('Activation code has expired. Please request a new one.');
      } else if (err.error === 'already_activated') {
        setError('This email is already activated. You can sign in now.');
        setTimeout(() => onActivationComplete(), 2000);
      } else {
        setError(err.message || 'Activation failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');
    setSuccess('');

    try {
      const result = await emailActivationService.resendActivationEmail(email);
      if (result.success) {
        setSuccess('New activation code sent! Please check your inbox.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend activation code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeInput = (value: string) => {
    const formatted = value.replace(/\s/g, '').toUpperCase();
    setActivationCode(formatted);
  };

  const renderEnterEmail = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Activate Your Account</h2>
        <p className="text-muted-foreground">
          Enter your email address to receive an activation code
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendActivation()}
            disabled={isLoading}
            autoFocus
          />
        </div>

        <Button
          onClick={handleSendActivation}
          className="w-full"
          disabled={!email || isLoading}
        >
          {isLoading ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
          ) : (
            <><Mail className="mr-2 h-4 w-4" />Send Activation Code</>
          )}
        </Button>
      </div>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Already have an activation code?{' '}
          <button
            onClick={() => setStep('enter-code')}
            className="text-primary hover:underline font-medium"
            disabled={isLoading}
          >
            Enter it here
          </button>
        </p>
      </div>
    </div>
  );

  const renderEnterCode = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Enter Activation Code</h2>
        <p className="text-muted-foreground">
          {isLoading
            ? 'Verifying your activation link…'
            : 'Enter your email and the 6-8 character code from your activation email'}
        </p>
      </div>

      {isLoading && (
        <div className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {!isLoading && (
        <>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Activation Code</Label>
              <Input
                id="code"
                type="text"
                placeholder="ABC123"
                value={activationCode}
                onChange={(e) => handleCodeInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleValidateCode()}
                disabled={isLoading}
                maxLength={8}
                className="text-center text-lg font-mono tracking-wider"
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-center">
                Enter the 6-8 character code from your email
              </p>
            </div>

            <Button
              onClick={handleValidateCode}
              className="w-full"
              disabled={!email || !activationCode || isLoading}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Activate Account
            </Button>

            <Button
              variant="outline"
              onClick={handleResendCode}
              className="w-full"
              disabled={!email || isResending || isLoading}
            >
              {isResending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Resending...</>
              ) : (
                <><RefreshCw className="mr-2 h-4 w-4" />Resend Code</>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderSuccess = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-green-900">Email Verified!</h2>
        <p className="text-muted-foreground mt-1">
          Your account has been activated successfully
        </p>
      </div>

      <Button variant="outline" onClick={onActivationComplete} className="w-full">
        Go to Sign In
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={onBackToLogin} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sign In
          </Button>

          <OptiBuildLogo size="lg" variant="full" className="mx-auto mb-6" />
        </div>

        <Card>
          <CardContent className="p-8">
            {step === 'enter-email' && renderEnterEmail()}
            {step === 'enter-code' && renderEnterCode()}
            {step === 'success' && renderSuccess()}
          </CardContent>
        </Card>

        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            Having trouble? Contact support at support@optibuild.com
          </p>
        </div>
      </div>
    </div>
  );
}
